import { useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { QuoteProvider } from "@/context/QuoteContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileBar from "@/components/StickyMobileBar";
import FloatingButtons from "@/components/FloatingButtons";
import HomePage from "@/pages/HomePage";
import PackagesPage from "@/pages/PackagesPage";
import PackageDetailPage from "@/pages/PackageDetailPage";
import ItinerariesPage from "@/pages/ItinerariesPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import WhyUsPage from "@/pages/WhyUsPage";
import ThankYouPage from "@/pages/ThankYouPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { captureUTMs } from "@/lib/utm";
import { initAnalytics } from "@/lib/analytics";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (hash) {
      const timer = setTimeout(() => {
        if (lenis) lenis.scrollTo(hash, { offset: -72 });
        else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return () => clearTimeout(timer);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

function App() {
  useEffect(() => {
    captureUTMs();
    initAnalytics();
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <QuoteProvider>
          <ScrollToTop />
          <Navbar />
          <div className="pb-16 md:pb-0">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/kashmir-tour-packages" element={<PackagesPage pageKey="kashmir-tour-packages" />} />
              <Route path="/kashmir-honeymoon-packages" element={<PackagesPage pageKey="kashmir-honeymoon-packages" />} />
              <Route path="/kashmir-family-packages" element={<PackagesPage pageKey="kashmir-family-packages" />} />
              <Route path="/kashmir-couple-packages" element={<PackagesPage pageKey="kashmir-couple-packages" />} />
              <Route path="/gulmarg-tour-packages" element={<PackagesPage pageKey="gulmarg-tour-packages" />} />
              <Route path="/pahalgam-tour-packages" element={<PackagesPage pageKey="pahalgam-tour-packages" />} />
              <Route path="/srinagar-tour-packages" element={<PackagesPage pageKey="srinagar-tour-packages" />} />
              <Route path="/kashmir-itineraries" element={<ItinerariesPage />} />
              <Route path="/packages/:slug" element={<PackageDetailPage />} />
              <Route path="/about-us" element={<AboutPage />} />
              <Route path="/why-youll-love-us" element={<WhyUsPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/contact-us" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<LegalPage pageKey="privacy-policy" />} />
              <Route path="/terms-and-conditions" element={<LegalPage pageKey="terms-and-conditions" />} />
              <Route path="/cancellation-refund-policy" element={<LegalPage pageKey="cancellation-refund-policy" />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          <Footer />
          <StickyMobileBar />
          <FloatingButtons />
          <Toaster position="top-center" richColors />
        </QuoteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
